import { useState } from "react";
import { normalizeWeeklySchedule, type WeeklySchedule, type DaySchedule } from "@emr/shared/scheduling";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Save } from "lucide-react";
import { useUpdateAppSettings } from "@/lib/mutations";

const DAYS: { key: keyof WeeklySchedule; label: string }[] = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

/**
 * Weekly schedule editor (Phase 4 simplification, 2026-10-18): off-day
 * toggles ONLY. The per-day morning/evening start-time inputs were removed —
 * session times now live in the effective-dated slot configs (Slots tab).
 * The API still stores morningStart/eveningStart in weekly_schedule (the
 * shared normalizeWeeklySchedule shape is unchanged); this editor simply no
 * longer edits them, preserving the stored values on save.
 */
export function WeeklyScheduleEditor({ schedule }: { schedule: Record<string, { off: boolean; morningStart: string; eveningStart: string }> | null | undefined }) {
  // Defensive init: a double-encoded/garbage weeklySchedule previously crashed
  // this editor (day.morningStart read off undefined → blank Settings page).
  // normalizeWeeklySchedule guarantees a complete 7-day object.
  const [draft, setDraft] = useState<WeeklySchedule>(() => normalizeWeeklySchedule(schedule));
  const update = useUpdateAppSettings();

  const updateDay = (key: keyof WeeklySchedule, patch: Partial<DaySchedule>) => {
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  };

  const save = async () => {
    try {
      await update.mutateAsync({ weeklySchedule: draft });
    } catch { /* handled */ }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500">Weekly Schedule</CardTitle>
        <Button onClick={save} disabled={update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
          <Save size={14} className="mr-1" /> Save
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
              <TableHead className="w-32">Day</TableHead>
              <TableHead className="w-24">Working</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {DAYS.map((d) => {
              const day = draft[d.key];
              return (
                <TableRow key={d.key} className="even:bg-cream-50/50">
                  <TableCell className="font-medium">{d.label}</TableCell>
                  <TableCell>
                    <Switch checked={!day.off} onCheckedChange={(checked) => updateDay(d.key, { off: !checked })} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <p className="mt-2 text-xs text-muted">
          Toggle a day off to mark it as a non-working day (no OPD). Session times and slots are configured per
          effective date in the <span className="font-medium">Slots</span> tab.
        </p>
      </CardContent>
    </Card>
  );
}
